import type { BoardDto, BoardStatus } from '@mm/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { listMyBoards, getMyProfile, type MyProfile } from '../api/my';
import { AppLayout } from '../layout/AppLayout';
import { cn } from '../lib/cn';
import { IconChevronRight } from '../ui/icons';

const STATUS_LABEL: Record<BoardStatus, string> = {
  NEW: 'Новое',
  VIEWED: 'Открыто',
  WORKING: 'В работе',
  FINISHED: 'Ждёт проверки',
  REVIEWED: 'Проверено',
};

function StatusBadge({ status }: { status: BoardStatus }) {
  const tone =
    status === 'REVIEWED'
      ? 'text-accent border-accent/40 bg-accent-soft'
      : status === 'FINISHED'
        ? 'text-fg border-fg/40'
        : status === 'WORKING'
          ? 'text-fg border-border'
          : 'text-muted border-border-soft';
  return (
    <span
      className={cn(
        'inline-block font-caps italic text-[11px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded border',
        tone,
      )}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

function formatDue(dueAt: string | null): string | null {
  if (!dueAt) return null;
  const d = new Date(dueAt);
  return d.toLocaleDateString('ru', { day: 'numeric', month: 'short' });
}

function formatTimeSpent(sec: number): string | null {
  if (sec <= 0) return null;
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} мин`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest === 0 ? `${h} ч` : `${h} ч ${rest} мин`;
}

export function MyBoardsPage() {
  const { user } = useAuth();
  const [boards, setBoards] = useState<BoardDto[] | null>(null);
  const [profile, setProfile] = useState<MyProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const firstName = user?.name.split(' ')[0] ?? '';

  useEffect(() => {
    let cancelled = false;
    Promise.all([listMyBoards(), getMyProfile()])
      .then(([b, p]) => {
        if (cancelled) return;
        setBoards(b.items);
        setProfile(p);
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить задания');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AppLayout crumbs={[{ label: 'Мой класс' }, { label: 'Главная' }]}>
      <div className="max-w-4xl px-8 py-10">
        <h1
          className="font-display text-[44px] leading-none font-medium mb-2"
          style={{ fontVariationSettings: '"opsz" 144, "SOFT" 15' }}
        >
          Здравствуйте, {firstName}
        </h1>
        <p className="font-caps italic text-[13px] text-muted mb-10">
          {profile ? `Репетитор — ${profile.tutor.name}` : 'Ваши задания'}
        </p>

        <h2
          className="font-display text-[22px] leading-none font-medium mb-4"
          style={{ fontVariationSettings: '"opsz" 48, "SOFT" 10' }}
        >
          Мои задания
        </h2>

        {error ? (
          <p className="text-[13px] text-accent border border-accent/30 bg-accent-soft px-4 py-3 rounded">
            {error}
          </p>
        ) : boards === null ? (
          <p className="text-muted text-[13px]">Загружаем…</p>
        ) : boards.length === 0 ? (
          <div className="rounded border-2 border-dashed border-border p-10 text-center">
            <span
              className="block font-display text-[56px] leading-none text-border mb-2"
              style={{ fontVariationSettings: '"opsz" 96' }}
            >
              ∅
            </span>
            <p className="text-muted text-[13px]">
              Пока заданий нет. Они появятся здесь после урока.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border-soft border-t border-b border-border-soft">
            {boards.map((b) => {
              const due = formatDue(b.dueAt);
              const time = formatTimeSpent(b.timeSpentSec);
              return (
                <li key={b.id}>
                  <Link
                    to={`/app/boards/${b.id}`}
                    className="flex items-center justify-between gap-4 py-3 px-1 hover:bg-row-hover rounded transition-colors"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2 mb-0.5">
                        <span className="block font-medium text-[15px] text-fg truncate">
                          {b.title}
                        </span>
                        <StatusBadge status={b.status} />
                      </span>
                      <span className="block text-[12px] text-muted">
                        {due ? <span>до {due}</span> : <span>без срока</span>}
                        {time ? (
                          <>
                            {' · '}
                            <span>⌛ {time}</span>
                          </>
                        ) : null}
                        {b.tutorComment ? (
                          <>
                            {' · '}
                            <span className="italic">есть комментарий</span>
                          </>
                        ) : null}
                      </span>
                    </span>
                    <IconChevronRight size={14} className="text-muted shrink-0" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </AppLayout>
  );
}
